import { ContainerManager, ContainerInitializer } from '../'

import {
  EdgeDockingPlugin,
  SnappingPlugin,
  StatePersistencePlugin
} from '../plugins'

import { ContentCreator, NotificationSystem, getState, StateInterface } from '../utils'
import { DemoContainerFactory } from './DemoContainerFactory'
import {
  AutoAdjustConfig,
  Boundaries,
  ContainerState,
  ContainerManagerInterface,
  DirectionMode,
  MovementMode,
  ResizeConfig,
} from '../core/types'

import { extendedMap } from '@alekstar79/utility'

interface ContainerCreationParams {
  x?: number;
  y?: number;
  width: number;
  height: number;
  content: string | HTMLElement | { template: string };
  type: 'string' | 'template' | 'element';
  title?: string;
  color?: string;
  draggingDirection?: DirectionMode;
  useSnapping?: boolean;
  boundaries?: Boundaries;
  constrainToParent?: boolean;
  autoAdjust?: AutoAdjustConfig;
  resize?: ResizeConfig;
  constrainToViewport?: boolean;
  containerId?: string;
  parentElement?: HTMLElement;
  restoreState?: boolean;
  isDemoContainer?: boolean;
  mode?: MovementMode;
}

export interface DemoContainer {
  manager: ContainerManagerInterface;
  element: HTMLElement;
  type: string;
  _uid: string;
  hasSnapping: boolean;
  containerId: string;
  isDemoContainer: boolean;
  maximizeState?: {
    isMaximized: boolean;
    originalState: ContainerState;
  };
}

export class ContainerOrchestrator
{
  public containers: DemoContainer[] = []
  private state: StateInterface = getState()
  private dIcons = ContainerOrchestrator.directionToIconMap()
  private maximizeStates: Map<string, {
    originalState: ContainerState;
    resizeHandler?: () => void;
  }> = new Map()

  constructor(
    private contentCreator: ContentCreator,
    private notificationSystem: NotificationSystem,
    private demoFactory: DemoContainerFactory,
    private isGlobalPinned: () => boolean,
    private onContainerCreated: (container: DemoContainer) => void,
    private onContainerClosed: (containerId: string, isDemo: boolean) => void
  ) {}

  static directionToIconMap() {
    return extendedMap<DirectionMode, string>({
      all: 'fa-up-down-left-right',
      horizontal: 'fa-left-right',
      vertical: 'fa-up-down'
    })
  }

  public async createContainer(params: ContainerCreationParams): Promise<HTMLElement>
  {
    const containerId = params.containerId!;
    const container = ContainerInitializer.createContainerElement(
      params.width,
      params.height,
      params.x,
      params.y,
      params.color
    )

    container.className = 'container advanced-container new'

    if (params.parentElement) {
      params.parentElement.appendChild(container);
    } else {
      document.body.appendChild(container);
    }

    const {
      dragHandle,
      pinButton,
      directionButton,
      maximizeButton,
      closeButton
    } = this.createEnhancedDragHandle(
      params.title ?? `Container ${this.containers.length + 1}`
    )

    container.appendChild(dragHandle)
    await this.contentCreator.createContent(params.content, container)
    this.state.push(containerId)

    container.dataset.containerType = params.type
    container.dataset.title = params.title || ''
    container.dataset.color = params.color || ''
    container.dataset.useSnapping = String(params.useSnapping || false)
    container.dataset.maximized = 'false'
    container.dataset.containerId = containerId

    const shouldConstrainToViewport = params.constrainToViewport ?? !params.constrainToParent
    const isDemoContainer = params.isDemoContainer || this.demoFactory.isDemoContainer(containerId)
    const initialMode = params.mode || (this.isGlobalPinned() ? 'pinned' : 'smooth')
    const initialDirection = params.draggingDirection || 'all'

    const manager = new ContainerManager(container, {
      _uid: containerId,
      mode: initialMode,
      boundaries: params.boundaries || {},
      constrainToViewport: shouldConstrainToViewport,
      draggingDirection: initialDirection,
      constrainToParent: params.constrainToParent || false,
      autoAdjust: params.autoAdjust || { enabled: false, width: false, height: false },
      resize: params.resize || { enabled: true, directions: ['se'] }
    }) as ContainerManagerInterface

    manager.setState({
      x: params.x || 0,
      y: params.y || 0,
      width: params.width,
      height: params.height
    })

    manager.use(new StatePersistencePlugin(), {
      containerId,
      isDemo: isDemoContainer
    })

    manager.use(new EdgeDockingPlugin(), {
      containerId,
      edgeThreshold: 30,
      visiblePeek: 20
    })

    if (params.useSnapping) {
      manager.use(new SnappingPlugin(), { snapStep: 30, enabled: true })
    }
    if (params.constrainToParent) {
      manager.recalculateForParent()
    }

    const demoContainer: DemoContainer = {
      _uid: containerId,
      containerId,
      element: container,
      hasSnapping: params.useSnapping || false,
      type: params.type,
      isDemoContainer,
      manager
    }

    this.setupContainerEvents(
      manager,
      container,
      pinButton,
      directionButton,
      maximizeButton,
      closeButton,
      containerId,
      initialMode,
      initialDirection
    )

    this.containers.push(demoContainer)
    this.onContainerCreated(demoContainer)

    setTimeout(() => {
      container.classList.remove('new')
    }, 300)

    container.addEventListener('click', () => {
      this.state.sort(containerId)

      this.containers.forEach(c => {
        c.element.style.zIndex = this.state.zIndex(c._uid)
      })
    })

    return container
  }

  private createEnhancedDragHandle(title: string): {
    dragHandle: HTMLElement;
    pinButton: HTMLButtonElement;
    directionButton: HTMLButtonElement;
    maximizeButton: HTMLButtonElement;
    closeButton: HTMLButtonElement;
  } {
    const dragHandle = document.createElement('div')
    dragHandle.className = 'drag-handle enhanced-drag-handle'
    dragHandle.setAttribute('data-drag-handle', 'true')

    const handleContent = document.createElement('div')
    handleContent.className = 'handle-content'

    const titleElement = document.createElement('span')
    titleElement.className = 'container-title'
    titleElement.textContent = title

    const modeControls = document.createElement('div')
    modeControls.className = 'mode-controls-container'

    const directionButton = document.createElement('button')
    directionButton.className = 'mode-btn direction-btn'
    directionButton.innerHTML = this.directionIcon()
    directionButton.title = 'Toggle Direction'
    directionButton.style.backgroundColor = 'transparent'
    modeControls.appendChild(directionButton)

    const maximizeButton = document.createElement('button')
    maximizeButton.className = 'mode-btn maximize-btn'
    maximizeButton.innerHTML = '<i class="fa-regular fa-window-maximize"></i>'
    maximizeButton.title = 'Maximize/Restore Container'
    maximizeButton.style.backgroundColor = 'transparent'
    modeControls.appendChild(maximizeButton)

    const pinButton = document.createElement('button')
    pinButton.className = 'mode-btn pin-btn'
    pinButton.innerHTML = this.pinIcon(this.isGlobalPinned())
    pinButton.title = 'Pin/Unpin Container'
    pinButton.style.backgroundColor = 'transparent'
    modeControls.appendChild(pinButton)

    const closeButton = document.createElement('button')
    closeButton.className = 'mode-btn close-btn'
    closeButton.innerHTML = '<i class="fa-solid fa-xmark"></i>'
    closeButton.title = 'Close Container'
    closeButton.style.backgroundColor = 'transparent'
    modeControls.appendChild(closeButton)

    handleContent.appendChild(titleElement)
    handleContent.appendChild(modeControls)
    dragHandle.appendChild(handleContent)

    return {
      dragHandle,
      pinButton,
      directionButton,
      maximizeButton,
      closeButton
    }
  }

  private pinIcon(islock = false): string
  {
    return `<i class="fa-solid fa-${islock ? 'lock' : 'lock-open'}"></i>`
  }

  private directionIcon(direction: DirectionMode = 'all')
  {
    return `<i class="fa-solid ${this.dIcons.get(direction)}"></i>`
  }

  private setupContainerEvents(
    manager: ContainerManagerInterface,
    container: HTMLElement,
    pinButton: HTMLButtonElement,
    directionButton: HTMLButtonElement,
    maximizeButton: HTMLButtonElement,
    closeButton: HTMLButtonElement,
    containerId: string,
    initialMode?: MovementMode,
    initialDirection?: DirectionMode
  ): void {
    this.syncContainerControlButtons(
      container,
      initialMode || manager.getMode(),
      initialDirection || manager.getDirection()
    )

    pinButton.addEventListener('click', (e) => {
      e.stopPropagation()
      e.preventDefault()

      const newMode: MovementMode = manager.getMode() === 'pinned' ? 'smooth' : 'pinned'
      manager.setMode(newMode)
      this.syncContainerControlButtons(container, newMode, manager.getDirection())
    })

    directionButton.addEventListener('click', (e) => {
      e.stopPropagation()
      e.preventDefault()

      const [nextDirection] = this.dIcons.nextEntries(manager.getDirection())
      manager.setDirection!(nextDirection)
      if (this.containers.find(c => c.containerId === containerId)?.isDemoContainer) {
        StatePersistencePlugin.updateContainerState(containerId, { draggingDirection: nextDirection })
      }

      this.updateContainerDirectionButton(container, nextDirection)
    })

    maximizeButton.addEventListener('click', (e) => {
      e.stopPropagation()
      e.preventDefault()

      this.toggleMaximize(manager, container, maximizeButton)
    })

    closeButton.addEventListener('click', (e) => {
      e.stopPropagation()
      e.preventDefault()

      this.closeContainer(manager, container)
    })

    // Prevent dragging on buttons
    ;[pinButton, directionButton, maximizeButton, closeButton]
      .forEach(button => {
        button.addEventListener('mousedown', e => e.stopPropagation())
        button.addEventListener('touchstart', e => e.stopPropagation())
      })
  }

  private closeContainer(manager: ContainerManagerInterface, container: HTMLElement): void
  {
    const containerIndex = this.containers.findIndex(c => c.manager === manager)
    if (containerIndex === -1) return

    const { containerId, isDemoContainer } = this.containers[containerIndex]

    if (isDemoContainer) {
      StatePersistencePlugin.saveContainerStateBeforeClose(manager, containerId)
    }

    container.style.animation = 'fadeOut 0.3s ease-out'
    setTimeout(() => {
      manager.destroy()
      this.removeMaximizeResizeHandler(container)
      container.parentNode?.removeChild(container)
      this.containers.splice(containerIndex, 1)
      this.onContainerClosed(containerId, isDemoContainer)
      this.notificationSystem.show('Container closed', 'info')
    }, 250)
  }

  private syncContainerControlButtons(
    container: HTMLElement,
    mode: MovementMode,
    direction: DirectionMode
  ): void {
    const pinButton = container.querySelector('.pin-btn') as HTMLButtonElement
    if (pinButton) {
      const icon = pinButton.querySelector('i')

      if (icon) {
        icon.classList.toggle('fa-lock', mode === 'pinned')
        icon.classList.toggle('fa-lock-open', mode !== 'pinned')
      }
    }

    this.updateContainerDirectionButton(container, direction)
    container.setAttribute('data-mode', mode)
  }

  public updateContainerDirectionButton(container: HTMLElement, direction: DirectionMode): void
  {
    const directionButton = container.querySelector('.direction-btn') as HTMLButtonElement
    if (!directionButton) return

    const icon = directionButton.firstElementChild
    if (!icon) return

    const allIcons = ['fa-up-down-left-right', 'fa-left-right', 'fa-up-down']
    allIcons.forEach(iconClass => icon.classList.remove(iconClass))
    const newIconClass = this.dIcons.get(direction)
    if (newIconClass) {
      icon.classList.add(newIconClass)
    }

    directionButton.title = `Movement Direction: ${direction}`
  }

  private toggleMaximize(
    manager: ContainerManagerInterface,
    container: HTMLElement,
    maximizeButton: HTMLButtonElement
  ): void {
    const isMaximized = container.dataset.maximized === 'true'
    if (isMaximized) {
      this.restoreContainer(manager, container, maximizeButton)
    } else {
      this.maximizeContainer(manager, container, maximizeButton)
    }
  }

  private maximizeContainer(
    manager: ContainerManagerInterface,
    container: HTMLElement,
    maximizeButton: HTMLButtonElement
  ): void {
    const containerId = container.dataset.containerId!
    const currentState = manager.getState()
    this.maximizeStates.set(containerId, { originalState: currentState })

    container.style.position = 'fixed'
    container.style.left = '0'
    container.style.top = '0'
    container.style.width = `${window.innerWidth}px`
    container.style.height = `${window.innerHeight}px`
    container.style.zIndex = '10000'
    container.dataset.maximized = 'true'

    const icon = maximizeButton.firstElementChild
    if (icon) {
      icon.classList.replace('fa-window-maximize', 'fa-window-restore')
    }

    manager.emitPluginEvent('maximizeChanged', { isMaximized: true, originalState: currentState })
    this.addMaximizeResizeHandler(container)
    this.toggleResizeHandles(container, false)
    this.notificationSystem.show('Container maximized', 'info')
  }

  private restoreContainer(
    manager: ContainerManagerInterface,
    container: HTMLElement,
    maximizeButton: HTMLButtonElement
  ): void {
    const containerId = container.dataset.containerId!
    const maximizeState = this.maximizeStates.get(containerId)
    if (maximizeState) {
      const { originalState } = maximizeState
      container.style.position = 'absolute'
      container.style.left = `${originalState.x}px`
      container.style.top = `${originalState.y}px`
      container.style.width = `${originalState.width}px`
      container.style.height = `${originalState.height}px`
      container.style.zIndex = ''
      manager.setState(originalState)
      this.maximizeStates.delete(containerId)
    }

    container.dataset.maximized = 'false'
    const icon = maximizeButton.firstElementChild
    if (icon) {
      icon.classList.replace('fa-window-restore', 'fa-window-maximize')
    }

    manager.emitPluginEvent('maximizeChanged', { isMaximized: false })
    this.removeMaximizeResizeHandler(container)
    this.toggleResizeHandles(container, true)
    this.notificationSystem.show('Container restored', 'info')
  }

  private addMaximizeResizeHandler(container: HTMLElement): void
  {
    const handler = () => {
      if (container.dataset.maximized === 'true') {
        container.style.width = `${window.innerWidth}px`
        container.style.height = `${window.innerHeight}px`
      }
    }

    const containerId = container.dataset.containerId
    if (containerId) {
      const maximizeState = this.maximizeStates.get(containerId)
      if (maximizeState) {
        maximizeState.resizeHandler = handler;
      }

      window.addEventListener('resize', handler)
    }
  }

  private removeMaximizeResizeHandler(container: HTMLElement): void
  {
    const containerId = container.dataset.containerId
    if (containerId) {
      const maximizeState = this.maximizeStates.get(containerId)
      if (maximizeState?.resizeHandler) {
        window.removeEventListener('resize', maximizeState.resizeHandler)
        maximizeState.resizeHandler = undefined
      }
    }
  }

  private toggleResizeHandles(container: HTMLElement, enabled: boolean): void
  {
    const resizeHandles = container.querySelectorAll('.resize-handle') as NodeListOf<HTMLElement>
    resizeHandles.forEach(handle => {
      handle.style.display = enabled ? 'block' : 'none'
      handle.style.pointerEvents = enabled ? 'auto' : 'none'
    })
  }

  public setAllContainersSnapStep(step: number): void
  {
    this.containers.forEach(({ manager, hasSnapping }) => {
      if (hasSnapping) {
        manager.setSnapStep?.(step)
      }
    })
  }

  public setAllContainersDirectionMode(direction: DirectionMode): void
  {
    this.containers.forEach(({ manager, element, containerId, isDemoContainer }) => {
      manager.setDirection(direction)

      if (isDemoContainer) {
        StatePersistencePlugin.updateContainerState(containerId, {
          draggingDirection: direction
        })
      }

      this.updateContainerDirectionButton(element, direction)
    })
  }

  public toggleGlobalPinnedMode(isPinned: boolean): void
  {
    const newMode: MovementMode = isPinned ? 'pinned' : 'smooth'
    this.containers.forEach(({ manager, element }) => {
      manager.setMode(newMode)
      this.syncContainerControlButtons(element, newMode, manager.getDirection())
    })
  }

  public destroyAll(): void
  {
    this.containers.forEach(({ manager }) => manager.destroy())
    this.containers = []

    document.querySelectorAll('.container')
      .forEach(el => el.remove())
  }
}
